/**
 * Status Action Options
 *
 * Configuration for status change action menu items.
 * Extracted from index.tsx row actions to centralize status change definitions.
 */

import { TrashIcon, XCircleIcon } from 'lucide-react';
import React from 'react';

import { getBaseRiskStatusOptions } from '@/components/legacy-ui/RiskDropdown';
import { RiskStatus } from '@/types';
import { getStatusLabel } from '@/utils/vulnerabilityStatus.util';

interface StatusOption {
  label: string;
  type?: 'label';
  icon?: React.JSX.Element;
  value?: string;
  isDelete?: boolean;
  isHardDelete?: boolean;
}

/**
 * Get status change menu options for a vulnerability.
 * Combines base status options with the Delete/Reject option.
 *
 * When `includeHardDelete` is true (Praetorian users only), also appends a
 * separate "Delete Permanently" option distinct from Reject. Reject closes a
 * finding with a status + reason (audit trail preserved); Delete Permanently
 * erases the record entirely.
 */
export function getStatusChangeOptions(
  includeHardDelete = false
): StatusOption[] {
  const availableStatusOptions = getBaseRiskStatusOptions();

  return [
    {
      label: 'Change Status',
      type: 'label',
    },
    ...availableStatusOptions.map(option => ({
      label: option.label,
      icon: option.icon,
      value: option.value,
    })),
    {
      label: getStatusLabel(RiskStatus.Deleted),
      icon: <XCircleIcon className="size-5" />,
      value: RiskStatus.Deleted,
      isDelete: true, // Flag to trigger delete modal
    },
    ...(includeHardDelete
      ? [
          {
            label: 'Delete Permanently',
            icon: <TrashIcon className="size-5" />,
            isHardDelete: true,
          },
        ]
      : []),
  ];
}
